import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Address } from '@pdqdev/pdq-shared/dist/entities';
import { FormattedAutoComplete } from '@app/sharedModule/components/address-autocomplete/address-autocomplete.component';
import { createAddressString } from '@utils/address-utils';

@Component({
  selector: 'fallback-address-input',
  templateUrl: './fallback-address-input.component.html',
  styleUrls: ['./fallback-address-input.component.scss'],
  standalone: false,
})
export class FallbackAddressInputComponent implements OnInit, OnDestroy {
  @Input() addressControl: AbstractControl;
  @Input() label = 'Default address (fallback)';

  fallbackAddressTextControl = new UntypedFormControl('');
  autocompleteRef: ElementRef;
  afterAutoCompleteSelect = false;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.initFallbackAddressCtrl();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initAutoCompleteRef(ref: ElementRef): void {
    this.autocompleteRef = ref;
  }

  onAutoCompleteSelected(values: FormattedAutoComplete): void {
    this.afterAutoCompleteSelect = true;
    const address: Partial<Address> & { description?: string } = {
      address1: values.street,
      city: values.city,
      province: values.state ?? values.state_abbreviation,
      zip: values.zip,
      country: values.country,
    };
    const addressDescription = createAddressString(address as Address, true);
    address.description = addressDescription;
    this.fallbackAddressTextControl.setValue(addressDescription, {
      emitEvent: false,
      onlySelf: true,
    });
    this.addressControl.patchValue(address);
    this.addressControl.markAsDirty();
  }

  clearFallbackAddress(): void {
    this.fallbackAddressTextControl.setValue('');
    this.addressControl.reset();
    this.addressControl.markAsDirty();
  }

  private initFallbackAddressCtrl(): void {
    const fallbackAddress = this.addressControl?.value;
    if (fallbackAddress) {
      const addressString = createAddressString(fallbackAddress as Address, true);
      if (addressString) {
        this.fallbackAddressTextControl.setValue(addressString);
      }
    }
    this.fallbackAddressTextControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const addressControl = this.fallbackAddressTextControl;
      const payloadControl = this.addressControl;
      if (
        !this.afterAutoCompleteSelect &&
        addressControl.dirty &&
        addressControl.value &&
        payloadControl.value?.description !== addressControl.value
      ) {
        addressControl.setErrors({ invalidAddress: true });
        addressControl.markAsDirty();
      } else {
        addressControl.setErrors(null);
      }
      this.afterAutoCompleteSelect = false;
    });
  }
}
